/** @file One clock tick: steps a running mix preview, else advances a playing playhead. */
import type { SkeletonData } from '@pose2d/core/types';
import { mixPreview } from './mixPreview';
import { mixPreviewStore, type MixRun } from './mixPreviewStore';
import { playhead } from './playhead';

/** The run to step on `data`: itself, or rebuilt when built on older data; null when an animation is gone. */
function runOn(run: MixRun, data: SkeletonData | null): MixRun | null {
  if (data === null || !data.animationIndex.has(run.from) || !data.animationIndex.has(run.to)) return null;
  return run.data === data ? run : mixPreview.rebuild(run, data);
}

/**
 * `data` is the compiled skeleton (null while none). A run built on older data is rebuilt on `data` first (its
 * AnimState indexes animations, which a recompile renumbers); one whose animations are gone (rename, delete) stops.
 */
export function playbackTick(dt: number, data: SkeletonData | null): void {
  const { run, tick } = mixPreviewStore.getState();
  if (run === null) {
    playhead.advance(dt);
    return;
  }
  const current = runOn(run, data);
  if (data === null || current === null) {
    mixPreviewStore.setState({ run: null });
    return;
  }
  mixPreview.step(current, data, dt);
  mixPreviewStore.setState({ run: current, tick: tick + 1 });
}
